import GlobalError from "Components/GlobalError/GlobalError";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import "./App.css";
import AppRoutes from "./AppRoutes";
import CookieConsent from "./Components/CookieConsent/CookieConsent";
import MaintenanceModal from "./Components/MaintenanceModal/MaintenanceModal";
import SessionExpirationModal from "./Components/SessionExpirationModal/SessionExpirationModal";
import "./i18n/index.js";
import { checkSession, logoutUser } from "./redux/Actions/authActions";

const RELEASE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELEASE_STORAGE_KEY = "algermanist_release_id";

const App = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const maintenanceEnabled =
    String(process.env.REACT_APP_MAINTENANCE_MODE).toLowerCase() === "true";

  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isAuthenticated);
  const profileName = useSelector((state) => state.auth.user.profileName);
  const roleName = useSelector((state) => state.auth.user.roleName);
  const roleNames = useSelector((state) => state.auth.user.roleNames);
  const sessionChecked = useSelector((state) => state.auth.sessionChecked);
  const sessionExpired = useSelector((state) => state.auth.sessionExpired);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logoutUser(false));
    localStorage.clear();
  };
  useEffect(() => {
    if (!sessionChecked && isLoggedIn) {
      dispatch(checkSession());
    }
  }, [dispatch, sessionChecked, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || sessionExpired) return undefined;

    let cancelled = false;
    const checkForNewRelease = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/release.json`, {
          cache: "no-store",
        });
        if (!response.ok) return;

        const release = await response.json();
        const releaseId = String(release?.id || "").trim();
        if (!releaseId || cancelled) return;

        const previousReleaseId = localStorage.getItem(RELEASE_STORAGE_KEY);
        if (previousReleaseId && previousReleaseId !== releaseId) {
          dispatch({ type: "SESSION_EXPIRED" });
          return;
        }
        localStorage.setItem(RELEASE_STORAGE_KEY, releaseId);
      } catch (_error) {
        // A temporary network error must not sign a user out.
      }
    };

    checkForNewRelease();
    const intervalId = window.setInterval(checkForNewRelease, RELEASE_CHECK_INTERVAL_MS);
    window.addEventListener("focus", checkForNewRelease);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkForNewRelease);
    };
  }, [dispatch, isLoggedIn, sessionExpired]);

  useEffect(() => {
    if (!maintenanceEnabled) return;
    if (maintenanceOpen) return;
    const handleAnyClick = () => setMaintenanceOpen(true);
    document.addEventListener("click", handleAnyClick, true);
    return () => {
      document.removeEventListener("click", handleAnyClick, true);
    };
  }, [maintenanceEnabled, maintenanceOpen]);

  const allowedRoutesWhenExpired = ["/", "/login", "/signup", "/verify"];
  const showSessionModal =
    sessionExpired && !allowedRoutesWhenExpired.includes(pathname) && !pathname.startsWith("/ausbildung/cv/") && !pathname.startsWith("/ausbildung/rooms/share/");

  return (
    <>
      <AppRoutes
        isLoggedIn={isLoggedIn}
        profileName={profileName}
        roleName={roleName}
        roleNames={roleNames}
        handleLogout={handleLogout}
      />
      {maintenanceEnabled && (
        <MaintenanceModal
          open={maintenanceOpen}
          onClose={() => setMaintenanceOpen(false)}
        />
      )}
      {showSessionModal && <SessionExpirationModal />}
      <GlobalError />
      <CookieConsent />
    </>
  );
};

export default App;
